import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { resolveImageUrl } from "../lib/imageUrl";

/**
 * Renders news / promo content with Markdown support + autolinked URLs.
 * - **жирный** / *курсив*
 * - `- пункт` маркированные списки
 * - `1. шаг` нумерованные списки
 * - `[текст](url)` ссылки
 * - Чистые `https://...` URL — превращаются в кликабельные ссылки (remark-gfm)
 * - Все переносы строк сохраняются (whiteSpace pre-wrap + breaks via newline=true)
 */
const ACCENT = "var(--ang-gold)";

const components = {
  a: ({ node, ...props }) => (
    <a {...props} target="_blank" rel="noopener noreferrer"
       style={{ color: ACCENT, textDecoration: "underline", textUnderlineOffset: "2px", wordBreak: "break-all" }} />
  ),
  ul: ({ node, ...props }) => (
    <ul {...props} style={{ paddingLeft: "20px", margin: "8px 0", listStyleType: "disc" }} />
  ),
  ol: ({ node, ...props }) => (
    <ol {...props} style={{ paddingLeft: "20px", margin: "8px 0" }} />
  ),
  li: ({ node, ...props }) => (
    <li {...props} style={{ marginBottom: "4px", color: "var(--ang-text-soft)" }} />
  ),
  p: ({ node, ...props }) => (
    <p {...props} style={{ margin: "0 0 8px 0", color: "var(--ang-text-muted)" }} />
  ),
  strong: ({ node, ...props }) => (
    <strong {...props} style={{ color: "var(--ang-text)", fontWeight: 700 }} />
  ),
  em: ({ node, ...props }) => (
    <em {...props} style={{ color: "var(--ang-text-soft)" }} />
  ),
  h1: ({ node, ...props }) => (
    <h1 {...props} style={{ fontFamily: "Cormorant Garamond", fontSize: "20px", color: "var(--ang-text)", fontWeight: 500, margin: "12px 0 6px" }} />
  ),
  h2: ({ node, ...props }) => (
    <h2 {...props} style={{ fontFamily: "Cormorant Garamond", fontSize: "18px", color: "var(--ang-text)", fontWeight: 500, margin: "10px 0 6px" }} />
  ),
  h3: ({ node, ...props }) => (
    <h3 {...props} style={{ fontFamily: "Cormorant Garamond", fontSize: "16px", color: ACCENT, fontWeight: 500, margin: "10px 0 4px" }} />
  ),
  blockquote: ({ node, ...props }) => (
    <blockquote {...props} style={{ borderLeft: `2px solid ${ACCENT}`, padding: "2px 12px", margin: "8px 0", color: "var(--ang-text-soft)", fontStyle: "italic" }} />
  ),
  code: ({ node, inline, ...props }) => (
    inline
      ? <code {...props} style={{ background: "var(--ang-border)", padding: "1px 5px", borderRadius: "3px", fontSize: "0.92em", color: ACCENT }} />
      : <pre style={{ background: "rgba(0,0,0,0.4)", border: "1px solid var(--ang-border)", padding: "10px 12px", borderRadius: "4px", overflow: "auto", fontSize: "12px", color: "var(--ang-text-soft)" }}><code {...props} /></pre>
  ),
  hr: () => (
    <hr style={{ border: "none", borderTop: "1px solid var(--ang-gold-soft)", margin: "12px 0" }} />
  ),
  img: ({ node, src, ...props }) => (
    <img {...props} src={resolveImageUrl(src)} alt={props.alt || ""} style={{ maxWidth: "100%", borderRadius: "4px", margin: "8px 0" }} />
  ),
};

export default function MarkdownContent({ text, className, style }) {
  if (!text) return null;
  return (
    <div className={className} style={{ fontFamily: "Manrope", fontSize: "13px", lineHeight: 1.65, color: "var(--ang-text-muted)", wordBreak: "break-word", ...style }}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {text}
      </ReactMarkdown>
    </div>
  );
}
